import REDIRECT_URI_WHITELIST from '@/constants/redirectUriWhitelist';
import { logError } from '@/helpers/logger/logUtils';

import { LOGIN_TYPES } from './enumsTypes';
import { PostMessageActions, sendPostMessage } from './postMessage';
import { setCookie } from './storage';

export interface AuthorizationData {
  tokenType: string;
  accessToken: string;
}

export interface Session {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  email: string;
  status?: string;
  userId?: string;
  tokenType?: string;
  isSocial?: boolean;
  keepLoggenIn?: boolean;
  userType?: string;
}

interface SignInSuccessParams {
  session: Session;
  loginType: LOGIN_TYPES;
}

const REACTIVATION_ALLOWED_STATUSES = {
  PENDING: 'PENDING',
  DEACTIVE: 'DEACTIVE',
} as const;

export const ACCOUNT_STATUSES = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
} as const;

export const isReactivationAllowed = (status: string): boolean =>
  status === REACTIVATION_ALLOWED_STATUSES.PENDING || status === REACTIVATION_ALLOWED_STATUSES.DEACTIVE;

export const signInSuccess = ({ session, loginType }: SignInSuccessParams): void => {
  signInWithCookie(session);
  sendPostMessage({ action: PostMessageActions.LOGIN_SUCCESS, payload: { loginType } });
};

export const signInWithCookie = (session: Session): void => {
  if (!session.accessToken) {
    logError('Access token is empty, cannot set cookie');
    return;
  }
  setCookie('token', session.accessToken, { path: '/', maxAge: session.expiresIn });
};

export const REDIRECT_URI = 'redirect_uri';

const LOCALHOST_REGEX = /^https?:\/\/localhost:\d{4}\//;

export const setRedirectUri = (redirectUri: string): void => {
  if (!redirectUri) return;

  const { hostname } = window.location;
  const decodedRedirectUri = window.decodeURIComponent(redirectUri);
  const isRedirectUriChanged = decodedRedirectUri !== sessionStorage.getItem(REDIRECT_URI);
  const isRedirectUriWhitelisted = REDIRECT_URI_WHITELIST.some((whitelistedRedirectUri) =>
    decodedRedirectUri.startsWith(whitelistedRedirectUri),
  );
  const isLocalhostRedirectUri = LOCALHOST_REGEX.test(redirectUri);
  const isLocalhostRedirectUriAllowed =
    (hostname === 'localhost' || hostname.startsWith('dev')) && isLocalhostRedirectUri;

  if (isRedirectUriChanged && (isRedirectUriWhitelisted || isLocalhostRedirectUriAllowed)) {
    sessionStorage.setItem(REDIRECT_URI, decodedRedirectUri);
  }
};

export const getRedirectUri = (): string =>
  sessionStorage.getItem(REDIRECT_URI) ?? `${window.location.origin}/accounts/v1/account/dashboard`;
